/**
 * Assessment Report Print Page
 *
 * AI-generated assessment report with print support.
 * Opens in new tab with printable HTML report.
 * Supports saving reports to database.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentReportApi, savedAssessmentReportsApi } from '../../lib/evaluationsApi';
import { useEvaluation } from './useEvaluations';

export function AssessmentReportPrintPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const evaluationId = Number(id);
  const { data: evaluation } = useEvaluation(evaluationId);

  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  const [showTranscriptInput, setShowTranscriptInput] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const generateReport = async (withTranscript?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const html = await assessmentReportApi.generateHtml(evaluationId, {
        transcript: withTranscript,
      });
      setHtmlContent(html);
      setShowTranscriptInput(false);
    } catch (err) {
      console.error('Failed to generate report:', err);
      setError('Verslag genereren mislukt. Controleer of er voldoende beoordelingsgegevens zijn.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Check if transcript was passed via sessionStorage
    const storedTranscript = sessionStorage.getItem(`assessment-transcript-${evaluationId}`);
    if (storedTranscript) {
      setTranscript(storedTranscript);
      sessionStorage.removeItem(`assessment-transcript-${evaluationId}`);
      // Generate with transcript
      generateReport(storedTranscript);
    } else {
      generateReport();
    }
  }, [evaluationId]);

  // Save report to database
  const handleSaveReport = async () => {
    if (!htmlContent || !evaluation) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      await savedAssessmentReportsApi.create({
        employeeId: evaluation.employeeId,
        evaluationId: evaluation.id,
        periodeStart: evaluation.periodeStart,
        periodeEnd: evaluation.periodeEnd,
        htmlContent,
        transcript: transcript || undefined,
      });

      // Navigate to Team page with employee selected and Verslagen tab open
      navigate(`/team?employeeId=${evaluation.employeeId}&tab=assessment-reports`);
    } catch (err) {
      console.error('Failed to save report:', err);
      setSaveMessage({ type: 'error', text: 'Opslaan mislukt. Probeer opnieuw.' });
      setIsSaving(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verslag Genereren Mislukt</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => generateReport()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Opnieuw Proberen
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Terug
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Beoordelingsverslag Genereren</h1>
          <p className="text-gray-600">AI analyseert de beoordeling...</p>
          <p className="text-sm text-gray-500 mt-2">Dit kan enkele seconden duren</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print Controls (hidden when printing) */}
      <div className="no-print fixed top-4 right-4 flex flex-col gap-2 z-50">
        {/* Save Status Message */}
        <div className="flex items-center gap-2 justify-end">
          {saveMessage && (
            <span
              className={`px-3 py-1 rounded-lg text-sm shadow-lg ${
                saveMessage.type === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {saveMessage.text}
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-1.5">
          <button
            onClick={() => setShowTranscriptInput(!showTranscriptInput)}
            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-lg flex items-center gap-1.5"
            title="Voeg gespreksnotities toe om het verslag te verrijken"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Transcript
          </button>
          <button
            onClick={() => {
              generateReport(transcript);
            }}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-lg flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Regenereren
          </button>
          <button
            onClick={handleSaveReport}
            disabled={isSaving || !htmlContent}
            className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 shadow-lg flex items-center gap-1.5 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {isSaving ? 'Opslaan...' : 'Opslaan'}
          </button>
          <button
            onClick={() => window.print()}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Afdrukken / PDF
          </button>
          <button
            onClick={() => navigate(`/beoordeling/${evaluationId}/wizard`)}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-lg"
          >
            Annuleren
          </button>
        </div>
      </div>

      {/* Transcript Input Panel */}
      {showTranscriptInput && (
        <div className="no-print fixed top-20 right-4 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-40 p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Gespreksnotities / Transcript</h3>
          <p className="text-sm text-gray-600 mb-3">
            Voeg optioneel gespreksnotities toe om het verslag te verrijken met extra context.
          </p>
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder="Plak hier gespreksnotities, transcriptie of andere relevante informatie..."
            className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => {
                generateReport(transcript);
              }}
              disabled={!transcript.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Verslag Genereren met Transcript
            </button>
            <button
              onClick={() => setShowTranscriptInput(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* Render HTML Content */}
      {htmlContent && (
        <div
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          className="assessment-report-content"
        />
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          /* Hide score badges in print */
          .score-badge, .score-display, [class*="score-badge"], [class*="ScoreDisplay"] { display: none !important; }
        }
        .assessment-report-content {
          /* Container for the injected HTML */
        }
      `}</style>
    </>
  );
}
